import { defineStore } from 'pinia'
import { getDocument, getCollection } from '../services/firestoreService'
import type { Vaccine, DoseDefinition, VaccineMetadata, VaccineDefinition } from '../types'
import { VACCINES, DOSES } from '../services/data'
import {
  SUPPORTED_VACCINE_RULE_ENGINE_VERSION,
  SUPPORTED_VACCINE_SCHEMA_VERSION,
  VACCINE_V2_DEFINITIONS_PATH,
} from '../config/vaccineSchema'

const CACHE_KEY_VACCINES = 'vaccine_definitions'
const CACHE_KEY_METADATA = 'vaccine_metadata'
const CACHE_KEY_DOSES = 'vaccine_doses'
const CACHE_KEY_SCHEMA_VERSION = 'vaccine_loaded_schema_version'

function mergeBuiltInSchemaAdditions(vaccines: Vaccine[], doses: DoseDefinition[]): boolean {
  let changed = false

  const vaccineIds = new Set(vaccines.map(v => v.id))
  for (const vaccine of VACCINES) {
    if (!vaccineIds.has(vaccine.id)) {
      vaccines.push(vaccine)
      changed = true
    }
  }

  const doseById = new Map(doses.map(dose => [dose.id, dose]))
  for (const builtInDose of DOSES) {
    const existingDose = doseById.get(builtInDose.id)
    if (!existingDose) {
      doses.push(builtInDose)
      changed = true
      continue
    }

    const schemaFields: Partial<DoseDefinition> = {}
    if (!existingDose.scheduleRule && builtInDose.scheduleRule) {
      schemaFields.scheduleRule = builtInDose.scheduleRule
    }
    if (!existingDose.completionRules && builtInDose.completionRules) {
      schemaFields.completionRules = builtInDose.completionRules
    }
    if (existingDose.legacyCompatible == null && builtInDose.legacyCompatible != null) {
      schemaFields.legacyCompatible = builtInDose.legacyCompatible
    }

    if (Object.keys(schemaFields).length > 0) {
      Object.assign(existingDose, schemaFields)
      changed = true
    }
  }

  return changed
}

export const useVaccineDefinitions = defineStore('vaccineDefinitions', {
  state: () => ({
    vaccines: [] as Vaccine[],
    doses: [] as DoseDefinition[],
    metadata: null as VaccineMetadata | null,
    loaded: false,
    hasUpdate: false,
    updateMessage: '',
    loadedSchemaVersion: 1,
  }),

  getters: {
    vaccineMap(state): Record<string, Vaccine> {
      return Object.fromEntries(state.vaccines.map(v => [v.id, v]))
    },

    doseMap(state): Record<string, DoseDefinition> {
      return Object.fromEntries(state.doses.map(d => [d.id, d]))
    },

    currentVersion(state): string {
      return state.metadata?.version || 'local'
    },
  },

  actions: {
    /**
     * 初始化疫苗定義資料
     * 1. 先從 localStorage 讀取（立即可用）
     * 2. 背景檢查 Firestore 版本
     * 3. 如果有更新，拉取新資料
     */
    async init() {
      // 步驟 1：從 localStorage 讀取快取
      this.loadFromCache()

      if (this.vaccines.length === 0) {
        this.vaccines = [...VACCINES]
        this.doses = [...DOSES]
        this.metadata = {
          version: 'local',
          updatedAt: new Date().toISOString(),
          source: 'Local Data',
        }
        this.loaded = true
        this.loadedSchemaVersion = SUPPORTED_VACCINE_SCHEMA_VERSION
      } else {
        const hasNewLocalData = mergeBuiltInSchemaAdditions(this.vaccines, this.doses)
        if (hasNewLocalData) {
          this.hasUpdate = true
          this.updateMessage = '疫苗資料已更新'
          this.saveToCache()
        }
      }

      // 步驟 2：背景檢查 Firestore 版本
      try {
        await this.checkForUpdates()
      } catch (error: any) {
        console.warn('⚠️ 無法檢查疫苗定義更新，將使用本地資料')
        console.warn('錯誤詳情:', {
          message: error?.message,
          code: error?.code,
          name: error?.name
        })
        if (error?.code === 'permission-denied') {
          console.warn('提示：請設定 Firestore Security Rules 以啟用雲端疫苗資料')
        }
        // 失敗不影響使用，繼續使用快取或本地資料
      }
    },

    /**
     * 從 localStorage 載入快取
     */
    loadFromCache() {
      try {
        const cachedVaccines = localStorage.getItem(CACHE_KEY_VACCINES)
        const cachedDoses = localStorage.getItem(CACHE_KEY_DOSES)
        const cachedMetadata = localStorage.getItem(CACHE_KEY_METADATA)

        if (cachedVaccines && cachedDoses && cachedMetadata) {
          this.vaccines = JSON.parse(cachedVaccines)
          this.doses = JSON.parse(cachedDoses)
          this.metadata = JSON.parse(cachedMetadata)
          this.loadedSchemaVersion = Number(localStorage.getItem(CACHE_KEY_SCHEMA_VERSION) ?? 1)
          this.loaded = true
        }
      } catch (error) {
        console.error('❌ 載入快取失敗:', error)
        // 清除損壞的快取
        this.clearCache()
      }
    },

    /**
     * 儲存到 localStorage
     */
    saveToCache() {
      try {
        localStorage.setItem(CACHE_KEY_VACCINES, JSON.stringify(this.vaccines))
        localStorage.setItem(CACHE_KEY_DOSES, JSON.stringify(this.doses))
        localStorage.setItem(CACHE_KEY_METADATA, JSON.stringify(this.metadata))
        localStorage.setItem(CACHE_KEY_SCHEMA_VERSION, String(this.loadedSchemaVersion))
      } catch (error) {
        console.error('❌ 儲存快取失敗:', error)
      }
    },

    /**
     * 清除快取
     */
    clearCache() {
      localStorage.removeItem(CACHE_KEY_VACCINES)
      localStorage.removeItem(CACHE_KEY_DOSES)
      localStorage.removeItem(CACHE_KEY_METADATA)
      localStorage.removeItem(CACHE_KEY_SCHEMA_VERSION)
    },

    /**
     * 檢查 Firestore 是否有更新
     */
    async checkForUpdates() {
      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('checkForUpdates 超時')), 5000)
        })

        const metaResult = await Promise.race([
          getDocument<VaccineMetadata>('vaccineMeta/current'),
          timeoutPromise
        ])

        if (!metaResult) {
          return
        }

        const cloudMetadata = metaResult as VaccineMetadata

        const localVersion = this.metadata?.version || 'local'
        const cloudVersion = cloudMetadata.version

        const needsSchemaUpgrade =
          cloudMetadata.schemaVersion === SUPPORTED_VACCINE_SCHEMA_VERSION &&
          this.loadedSchemaVersion < SUPPORTED_VACCINE_SCHEMA_VERSION

        if (localVersion !== cloudVersion || needsSchemaUpgrade) {
          await this.fetchFromFirestore()
          this.hasUpdate = true
          this.updateMessage = `官方疫苗資料已更新至 ${cloudVersion} 版`
        }
      } catch (error) {
        console.error('❌ 檢查更新失敗:', error)
        throw error
      }
    },

    /**
     * 從 Firestore 拉取最新資料
     */
    async fetchFromFirestore() {
      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('fetchFromFirestore 超時')), 10000)
        })

        // 1. 讀取元資料
        const metaResult = await Promise.race([
          getDocument<VaccineMetadata>('vaccineMeta/current'),
          timeoutPromise
        ])

        if (!metaResult) {
          throw new Error('找不到疫苗定義元資料')
        }

        const metadata = metaResult as VaccineMetadata

        // 2. 新 App 優先讀 v2；後端尚未發布 v2 或能力不足時讀 legacy。
        const canUseV2 =
          metadata.schemaVersion === SUPPORTED_VACCINE_SCHEMA_VERSION &&
          (metadata.ruleEngineVersion ?? 0) <= SUPPORTED_VACCINE_RULE_ENGINE_VERSION
        let defs: VaccineDefinition[]
        let loadedSchemaVersion = 1
        try {
          defs = await Promise.race([
            getCollection<VaccineDefinition>(canUseV2 ? VACCINE_V2_DEFINITIONS_PATH : 'vaccineDefinitions'),
            timeoutPromise
          ])
          if (canUseV2 && defs.length === 0) throw new Error('v2 疫苗定義為空')
          loadedSchemaVersion = canUseV2 ? SUPPORTED_VACCINE_SCHEMA_VERSION : 1
        } catch (error) {
          if (!canUseV2) throw error
          console.warn('[VaccineDefinitions] v2 載入失敗，回退 legacy 定義', error)
          defs = await getCollection<VaccineDefinition>('vaccineDefinitions')
          loadedSchemaVersion = 1
        }

        const vaccines: Vaccine[] = []
        const doses: DoseDefinition[] = []

        for (const def of defs) {
          vaccines.push({
            id: def.id,
            name: def.name,
            fullName: def.fullName,
            type: def.type,
            description: def.description,
            sideEffects: def.sideEffects,
            contraindications: def.contraindications,
            notes: def.notes,
          })

          if (def.doses) {
            doses.push(...def.doses)
          }
        }

        const hasBuiltInSchemaAdditions =
          loadedSchemaVersion < SUPPORTED_VACCINE_SCHEMA_VERSION &&
          mergeBuiltInSchemaAdditions(vaccines, doses)

        // 3. 更新狀態
        this.vaccines = vaccines
        this.doses = doses
        this.metadata = metadata
        this.loadedSchemaVersion = loadedSchemaVersion
        this.loaded = true

        if (hasBuiltInSchemaAdditions) {
          this.hasUpdate = true
          this.updateMessage = `官方疫苗資料已更新至 ${metadata.version} 版`
        }

        // 4. 儲存到快取
        this.saveToCache()

      } catch (error) {
        console.error('❌ 從 Firestore 載入失敗:', error)
        throw error
      }
    },

    /**
     * 清除更新標記
     */
    clearUpdateFlag() {
      this.hasUpdate = false
      this.updateMessage = ''
    },

    /* =========================
       查詢 API（相容 data.ts）
    ========================= */

    getVaccineById(vaccineId: string): Vaccine | undefined {
      return this.vaccineMap[vaccineId]
    },

    getDoseById(doseId: string): DoseDefinition | undefined {
      return this.doseMap[doseId]
    },

    getDosesByVaccineId(vaccineId: string): DoseDefinition[] {
      return this.doses.filter(d => d.vaccineId === vaccineId)
    },

    getTotalDosesCount(vaccineId: string): number {
      return this.doses.filter(d => d.vaccineId === vaccineId).length
    },

    /**
     * 取得所有疫苗定義（相容 VACCINES）
     */
    getAllVaccines(): Vaccine[] {
      return this.vaccines
    },

    /**
     * 取得所有劑次定義（相容 DOSES）
     */
    getAllDoses(): DoseDefinition[] {
      return this.doses
    },
  },
})
